import React from "react";
import { ScrollView, StyleSheet, Text, TextInput, View } from "react-native";
import { useSelector } from "react-redux";
import moment from "moment";

import { kColors } from "../../kColors";
import {
  addPromotion,
  brandAddAnnouncement,
  brandEditAnnouncement,
  cancelPromotion,
  updatePromotion,
} from "src/redux/company/actions";
import { fonts } from "src/fonts";
import { tAnnouncement, tAnnouncementRequestBody, tPromotion } from "tmt_api/src/types/types.validator";
import { PdDateInput } from "src/components/forms/input";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { tUseModal, useModal } from "src/providers/OverlayProvider";
import { PdModal, PdModalCloseButton, PdModalTitle } from "src/components/overlay";
import { tModalComponentProps } from "src/redux/modals/types";
import { ComboBox, NumberInput } from "src/components";
import { CheckMark2 } from "src/components/NewCheckBox2";
import { PdStack, PdText } from "src/components/layout";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { useAppDispatch } from "src/redux";
import { useIsMobile } from "../ui";
import { PdRadio, PdRadioGroup } from "src/components/forms/radio";
import { FormProvider, useForm } from "react-hook-form";
import { PdFormLabel } from "src/components/form-control";
import { PdHookFormDateRangeInput, PdHookFormErrorMessage, PdHookFormInput } from "src/components/hook-form";
import { getApiError } from "src/util/apiNew";

type tPromotionModalProps = {
  promotion?: tPromotion;
  announcement?: tAnnouncement;
};

type tAnnouncementFormData = {
  period: [Date, Date];
  title: string;
  description: string;
};

const getStatus = (endDate: number, deactivated?: boolean): "Expired" | "Active" => {
  if (deactivated || Date.now() > endDate) return "Expired";
  return "Active";
};

const PromotionForm: React.FC<Pick<tModalComponentProps<tPromotionModalProps>, "promotion" | "onResolve">> = ({
  promotion,
  onResolve,
}) => {
  const isMobile = useIsMobile();
  const rBrandId = useSelector(({ CompanyReducer }) => (CompanyReducer.type === "brand" ? CompanyReducer.id : ""));
  const [sPromoName, _setPromoName] = React.useState<string>(promotion?.name ?? "");
  const [sPromoDescription, _setPromoDescription] = React.useState<string>(promotion?.description ?? "");
  const [sCodeValue, _setCodeValue] = React.useState<string>(promotion?.code ?? "");
  const [sIsPrivate, _setIsPrivate] = React.useState(promotion?.isPrivate ?? false);
  const [sIsDiscount, _setIsDiscount] = React.useState(promotion?.discountPercent ? true : false);
  const [sStartDate, _setStartDate] = React.useState(promotion?.startDate ? new Date(promotion.startDate) : new Date());
  const [sEndDate, _setEndDate] = React.useState(
    promotion?.endDate ? new Date(promotion.endDate) : moment().add(1, "month").toDate(),
  );
  const [sDiscountPercent, _setDiscountPercent] = React.useState<number>(
    promotion?.discountPercent ? promotion?.discountPercent * 100 : 0,
  );
  const [sDiscountFixed, _setDiscountFixed] = React.useState<number>(promotion?.discountFixed ?? 0);
  const [sEnableAt, _setEnableAt] = React.useState<number>(promotion?.enableAt ?? 0);

  const dispatch = useAppDispatch();
  const status = promotion?.endDate ? getStatus(promotion?.endDate, promotion?.deactivated) : "Expired";

  const handleOnChangeTextCode = (newCode: string) => {
    _setCodeValue(newCode.replace(/[^a-zA-Z0-9]/g, ""));
  };

  const handleDeactivate = async () => {
    if (!promotion) return;
    await dispatch(cancelPromotion(promotion.id, promotion));
    onResolve();
  };

  const handleAddPromo = async () => {
    if (
      !sCodeValue ||
      !sStartDate ||
      !sEndDate ||
      sEnableAt === undefined ||
      (sIsDiscount ? !sDiscountPercent : !sDiscountFixed) ||
      !sPromoName
    ) {
      dispatch({ type: "RIBBON", message: new Error("Please fill the mandatory fields") });
      return;
    }

    await dispatch(
      addPromotion({
        name: sPromoName,
        description: sPromoDescription,
        code: sCodeValue.toLowerCase(),
        brandId: rBrandId,
        enableAt: sEnableAt,
        id: "",
        ordersApplied: [],
        timesUsed: 0,
        deactivated: false,
        discountFixed: !sIsDiscount ? sDiscountFixed : 0,
        startDate: moment(sStartDate).startOf("day").valueOf(),
        endDate: moment(sEndDate).endOf("day").valueOf(),
        discountPercent: sIsDiscount ? sDiscountPercent / 100 : 0,
        isPrivate: sIsPrivate,
      }),
    );

    onResolve();
  };

  const handleUpdatePromo = async () => {
    if (!promotion) return;
    if (
      !sCodeValue ||
      !sStartDate ||
      !sEndDate ||
      !sEnableAt ||
      (sIsDiscount ? !sDiscountPercent : !sDiscountFixed) ||
      !sPromoName
    ) {
      dispatch({ type: "RIBBON", message: new Error("Please fill the mandatory fields") });
      return;
    }

    await dispatch(
      updatePromotion(promotion.id, {
        name: sPromoName,
        description: sPromoDescription,
        code: sCodeValue.toLowerCase(),
        brandId: rBrandId,
        enableAt: sEnableAt,
        id: promotion.id ?? "",
        ordersApplied: promotion.ordersApplied ?? [],
        timesUsed: promotion.timesUsed ?? 0,
        deactivated: false,
        discountFixed: !sIsDiscount ? sDiscountFixed : 0,
        startDate: moment(sStartDate).startOf("day").valueOf(),
        endDate: moment(sEndDate).endOf("day").valueOf(),
        discountPercent: sIsDiscount ? sDiscountPercent / 100 : 0,
        isPrivate: sIsPrivate,
      }),
    );

    onResolve();
  };

  const handleSetAvailability = React.useCallback(
    ({ myValue }) => _setIsPrivate(myValue === "Private"),
    [_setIsPrivate],
  );
  const handleSetIsDiscountFalse = React.useCallback(() => _setIsDiscount(false), [_setIsDiscount]);
  const handleSetIsDiscountTrue = React.useCallback(() => _setIsDiscount(true), [_setIsDiscount]);

  const handleOnChangeStartPeriod = (newPeriod: [Date, Date]) => {
    _setStartDate(newPeriod[0]);
    _setEndDate(newPeriod[1]);
  };

  return (
    <>
      <PdModalTitle style={styles.title}>Create a Promotion</PdModalTitle>
      <PdText>Select options and start date for your promotion</PdText>
      <View style={[styles.gFormRowNoMargin, styles.zIndexFourteen]}>
        <View style={styles.gLinkBankFormItem}>
          <Text style={styles.P08_01_InputHeader}>Promotion Code*</Text>
          <TextInput
            onChangeText={handleOnChangeTextCode}
            placeholder="PROMO1"
            value={sCodeValue}
            style={styles.gLinkBankInput}
          />
        </View>
        <View style={styles.gLinkBankFormItemNoRightMargin}>
          <Text style={styles.P08_01_InputHeader}>Visibility*</Text>
          <ComboBox
            value={sIsPrivate ? "Private" : "Public"}
            onSelect={handleSetAvailability}
            myOptions={[{ myValue: "Public" }, { myValue: "Private" }]}
            myPlaceholder="Public"
          />
        </View>
      </View>
      <View style={styles.periodFieldRow}>
        <Text style={styles.P08_01_InputHeader}>Period*</Text>
        <PdDateInput
          style={styles.periodDateInput}
          startDate={sStartDate}
          endDate={sEndDate}
          onChange={handleOnChangeStartPeriod}
          selectsRange
        />
      </View>
      <View style={styles.gLinkBankFormItemNoRightTopMargin}>
        <Text style={styles.P08_01_InputHeader}>Minimum Order*</Text>
        <NumberInput
          style={styles.gLinkBankInput}
          unit={{ label: "$", location: "start" }}
          placeholder={sEnableAt.toString()}
          onChangeNumber={_setEnableAt}
        />
      </View>
      <Text style={styles.P08_01_InputHeader}>Product Discount*</Text>
      <View style={styles.gFormRowNoMarginLeft}>
        <CheckMark2
          myText="Fixed"
          style={styles.gLinkBankInput}
          onPress={handleSetIsDiscountFalse}
          textStyle={[styles.marginLeft, fonts.H3_BrandDark]}
          myState={!sIsDiscount}
        />
        <NumberInput
          style={styles.gLinkBankInputTiny}
          textInputStyle={styles.P05_PromotionModalWidth}
          unit={{ label: "$", location: "start" }}
          placeholder={sDiscountFixed.toString()}
          onChangeNumber={_setDiscountFixed}
        />
      </View>
      <View style={styles.gFormRow}>
        <CheckMark2
          myText="Percent"
          style={styles.gLinkBankInput}
          onPress={handleSetIsDiscountTrue}
          textStyle={[styles.marginLeft, fonts.H3_BrandDark]}
          myState={sIsDiscount}
        />
        <NumberInput
          style={styles.gLinkBankInputTiny}
          unit={{ label: "%", location: "end" }}
          placeholder={sDiscountPercent.toString()}
          onChangeNumber={_setDiscountPercent}
        />
      </View>
      <View style={styles.gFormRow}>
        <Text style={styles.P08_01_InputHeader}>Promotion Title*</Text>
      </View>
      <View style={styles.gFormRow}>
        <TextInput
          placeholder={promotion?.name ? promotion?.name : "ex: Holiday 2023!"}
          placeholderTextColor={kColors.mediumGrey}
          onChangeText={_setPromoName}
          maxLength={37}
          multiline={false}
          numberOfLines={1}
          style={styles.mPromoModalDescription}
        />
      </View>
      <View style={styles.gFormRow}>
        <Text style={styles.P08_01_InputHeader}>Promotion Description</Text>
      </View>
      <View style={styles.gFormRow}>
        <TextInput
          placeholder={
            promotion?.description ? promotion?.description : "ex: Purchase must include a case of Vanilla candles"
          }
          placeholderTextColor={kColors.mediumGrey}
          onChangeText={_setPromoDescription}
          maxLength={91}
          multiline={false}
          numberOfLines={1}
          style={styles.mPromoModalDescription}
        />
      </View>
      <PdStack
        direction={isMobile ? "vertical" : "horizontal"}
        layout={[1, 1]}
        gap={3}
        style={[isMobile ? styles.flexReverse : {}, styles.buttonGroup]}
      >
        {status === "Active" && (
          <PdButton onPress={handleDeactivate} variant="outline">
            <PdButtonLabel>Deactivate</PdButtonLabel>
          </PdButton>
        )}
        {promotion?.id ? (
          <PdButton onPress={handleUpdatePromo} style={isMobile ? styles.mb4 : {}}>
            <PdButtonLabel>Update</PdButtonLabel>
          </PdButton>
        ) : (
          <PdButton onPress={handleAddPromo} style={isMobile ? styles.mb4 : {}}>
            <PdButtonLabel>Activate</PdButtonLabel>
          </PdButton>
        )}
      </PdStack>
    </>
  );
};

const AnnouncementForm: React.FC<Pick<tModalComponentProps<tPromotionModalProps>, "announcement" | "onResolve">> = ({
  announcement,
  onResolve,
}) => {
  const rBrandId = useSelector((state) => (state.CompanyReducer.type === "brand" ? state.CompanyReducer.id : ""));
  const dispatch = useAppDispatch();
  const formMethods = useForm<tAnnouncementFormData>({
    defaultValues: {
      period: announcement
        ? [new Date(announcement.startDate.seconds * 1000), new Date(announcement.endDate.seconds * 1000)]
        : [moment().toDate(), moment().add(1, "month").toDate()],
      title: announcement?.title ?? "",
      description: announcement?.description ?? "",
    },
  });
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      setSubmitting(true);

      const payload: tAnnouncementRequestBody = {
        startDateInMillis: moment(formData.period[0]).startOf("day").valueOf(),
        endDateInMillis: moment(formData.period[1]).endOf("day").valueOf(),
        title: formData.title,
        description: formData.description,
      };

      if (announcement?.id) {
        await dispatch(brandEditAnnouncement(rBrandId, announcement.id, payload));
      } else {
        await dispatch(brandAddAnnouncement(rBrandId, payload));
      }

      dispatch({ type: "RIBBON", message: "Announcement created" });

      onResolve();
    } catch (error) {
      setSubmitting(false);
      dispatch({ type: "RIBBON", message: new Error(getApiError(error).message) });
    }
  });

  return (
    <FormProvider {...formMethods}>
      <PdModalTitle style={styles.title}>Create an Announcement</PdModalTitle>
      <PdText style={styles.mb1}>{"Have an announcement you'd like your Retailers to know about?"}</PdText>
      <PdText style={styles.mb4} variant="body-sm">
        Simply write the information below and publish. The announcement will be visible to Retailers who visit your
        brand page.
      </PdText>
      <View style={styles.periodFormControl}>
        <PdFormLabel>Period *</PdFormLabel>
        <PdHookFormDateRangeInput containerStyle={styles.mrAuto} name="period" rules={{ required: "Required" }} />
        <PdHookFormErrorMessage name="period" />
      </View>
      <View style={styles.mb3}>
        <PdFormLabel>Announcement Title *</PdFormLabel>
        <PdHookFormInput placeholder="ex: NEW HOLIDAY COLLECTION!" name="title" rules={{ required: "Required" }} />
        <PdHookFormErrorMessage name="title" />
      </View>
      <View style={styles.mb3}>
        <PdFormLabel>Announcement Description *</PdFormLabel>
        <PdHookFormInput
          placeholder="ex: Our new Holiday Collection is now available to ship. The new collection includes 6 new ornaments in various metallic colors. Shop below!"
          name="description"
          multiline={true}
          numberOfLines={8}
          rules={{ required: "Required" }}
        />
        <PdHookFormErrorMessage name="description" />
      </View>
      <PdButton style={styles.mlAuto} onPress={handleOnSubmit} isLoading={isSubmitting}>
        <PdButtonLabel>Save & Publish</PdButtonLabel>
      </PdButton>
    </FormProvider>
  );
};

export const PromotionModal: React.FC<tModalComponentProps<tPromotionModalProps>> = ({
  promotion,
  announcement,
  onClose,
  onResolve,
}) => {
  const isMobile = useIsMobile();
  const [type, setType] = React.useState(announcement ? "announcement" : "promotion");

  const handleOnClose = () => {
    onClose();
  };

  return (
    <PdModal style={styles.modal} onClose={onClose}>
      <PdModalCloseButton onPress={handleOnClose} />
      <ScrollView contentContainerStyle={styles.modalBody}>
        <PdRadioGroup value={type} onChange={setType}>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} style={{ marginBottom: 4 * kStyleConst.kSpacing }}>
            <PdText>I am creating:</PdText>
            <PdRadio value="promotion">a Promotion</PdRadio>
            <PdRadio value="announcement">an Announcement</PdRadio>
          </PdStack>
        </PdRadioGroup>
        {type === "promotion" && <PromotionForm promotion={promotion} onResolve={onResolve} />}
        {type === "announcement" && <AnnouncementForm announcement={announcement} onResolve={onResolve} />}
      </ScrollView>
    </PdModal>
  );
};

const usePromotionModal = (): tUseModal<tPromotionModalProps, unknown> => {
  return useModal(PromotionModal);
};

export default usePromotionModal;

const styles = StyleSheet.create({
  P05_PromotionModalWidth: {
    width: 20,
  },
  P08_01_InputHeader: {
    ...kFonts.formLegend,
  },
  buttonGroup: {
    marginTop: 6 * kStyleConst.kSpacing,
    width: "100%",
  },
  flexReverse: {
    flexDirection: "column-reverse",
  },
  gFormRow: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  gFormRowNoMargin: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    minWidth: kStyleConst.kApplyMinWidth,
  },
  gFormRowNoMarginLeft: {
    alignItems: "flex-start",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 3 * kStyleConst.kSpacing,
    marginLeft: 0,
    minWidth: 300,
  },
  gLinkBankFormItem: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginRight: 8 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoRightMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
    marginTop: 6 * kStyleConst.kSpacing,
  },
  gLinkBankFormItemNoRightTopMargin: {
    flexGrow: 1,
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  gLinkBankInput: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    flex: 1,
    height: 36,
    marginLeft: 0,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  gLinkBankInputTiny: {
    borderBottomWidth: 1,
    borderColor: kColors.brandPink,
    borderRadius: 2,
    height: 36,
    marginTop: 5,
    outlineColor: kColors.transparent,
    paddingLeft: kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
    width: 70,
  },
  mPromoModalDescription: {
    borderColor: kColors.brandPink,
    borderRadius: 2,
    borderWidth: 1,
    flex: 1,
    height: 40,
    marginTop: 5,
    minWidth: kStyleConst.kApplyMinWidth,
    outlineColor: kColors.transparent,
    outlineWidth: 0,
    paddingLeft: 2 * kStyleConst.kSpacing,
    paddingVertical: 2 * kStyleConst.kSpacing,
  },
  marginLeft: {
    marginLeft: 16,
  },
  mb1: {
    marginBottom: 1 * kStyleConst.kSpacing,
  },
  mb3: {
    marginBottom: 3 * kStyleConst.kSpacing,
  },
  mb4: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  modal: {
    maxWidth: 600,
    width: "100%",
  },
  modalBody: {
    paddingHorizontal: 6 * kStyleConst.kSpacing,
    paddingVertical: 8 * kStyleConst.kSpacing,
  },
  mrAuto: {
    marginRight: "auto",
  },
  periodDateInput: {
    borderBottomWidth: 1,
    borderWidth: 0,
    marginTop: 2 * kStyleConst.kSpacing,
    paddingLeft: kStyleConst.kSpacing,
    width: "100%",
  },
  periodFieldRow: {
    marginBottom: 3 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  periodFormControl: {
    marginBottom: 3 * kStyleConst.kSpacing,
    zIndex: 1,
  },
  title: {
    marginBottom: 4 * kStyleConst.kSpacing,
  },
  zIndexFourteen: {
    zIndex: 14,
  },
});
